import userRole from './userRole'
import branding from './branding'

export const getRoleTitle = (role) => {
  switch (role) {
    case userRole.MANAGER:
      return `${branding.NAME} Manager`
    case userRole.OPERATOR:
      return `${branding.NAME} Operator`
    case userRole.STAFF:
      return 'Team Member'
    case userRole.CREATIVE_MANAGER:
      return 'Creative Manager'
    case userRole.PRODUCTION_MANAGER:
      return 'Production Manager'
    default:
      return 'Account Owner'
  }
}

/**
 * Corporate staff can be granted admin rights by the whitelabel account owner
 * (stored as `options.adminRight` on the customer record, exposed as
 * `adminRight` on the profile payload). Those staff are treated as the
 * whitelabel account everywhere a role is checked.
 */
export const hasAdminRight = (user) =>
  Boolean(user && user.role === userRole.CORPORATE_STAFF && user.adminRight)

export const getEffectiveRole = (user) =>
  hasAdminRight(user) ? userRole.WHITELABEL : user && user.role

/**
 * Setting the admin rights flag stays with the whitelabel account owner. A
 * corporate staff granted admin rights keeps every other corporate staff task
 * but cannot promote anyone, including itself, so the checkbox is hidden for
 * them and the value is never sent from their form.
 */
export const canGrantAdminRight = (user) =>
  Boolean(user && user.role && user.role !== userRole.CORPORATE_STAFF)

/**
 * Deleting a staff who holds admin rights is a de facto revoke, so it stays
 * with the account owner for the same reason the checkbox does. Corporate staff
 * keep the remove action for every non-admin staff.
 */
export const canRemoveCorporateStaff = (user, staff) =>
  canGrantAdminRight(user) || !(staff && staff.adminRight)

export const checkOrderCreator = (creatorId) => {
  const userId = localStorage.getItem('userId')

  return userId === creatorId
}

export const checkOrderCollaborator = (collaborator = []) => {
  const userId = localStorage.getItem('userId')

  const index = collaborator.findIndex((c) => c.id === userId)

  return index > -1
}

export const checkAbilityOrder = (orderDetail, isAbleToUpdate) => {
  if (
    (orderDetail && checkOrderCreator(orderDetail.creator.id)) ||
    isAbleToUpdate
  ) {
    return true
  }

  return false
}

export const checkAbilityOrderCollaborator = (orderDetail, isAbleToUpdate) => {
  if (
    (orderDetail && checkOrderCreator(orderDetail.creator.id)) ||
    (orderDetail && checkOrderCollaborator(orderDetail.collaborators)) ||
    isAbleToUpdate
  ) {
    return true
  }

  return false
}

export const checkAbilityDeleteOrderAttachment = (
  user,
  uploadedBy,
  orderDetail,
  isAbleToUpdate
) => {
  if (
    (orderDetail && checkOrderCreator(orderDetail.creator.id)) ||
    isAttachmentUploadedByUser(user, uploadedBy) ||
    isAbleToUpdate
  ) {
    return true
  }

  return false
}

export const isAttachmentUploadedByUser = (user, uploadedBy) => {
  if (`${user.firstname} ${user.lastname}` === uploadedBy) {
    return true
  }

  return false
}

export const isAttachmentUploadedByTypeUserCustomer = (uploadedByType) => {
  if (uploadedByType === 'customer') {
    return true
  }

  return false
}

export const checUserTypeOrder = (orderDetail) => {
  if (checkOrderCreator(orderDetail.creator.id)) {
    return 'creator'
  } else if (checkOrderCollaborator(orderDetail.collaborators)) {
    return 'collaborator'
  }

  return 'guest'
}

/**
 * Attachments used to be downloadable by customers only once the order reached
 * in-review / approved / completed. That restriction is gone: the annotation
 * viewer hands over the original PDF at any progress, so gating the order
 * detail download no longer withheld anything.
 */
export const checkAbilityDownloadOrderAttachment = () => true

// https://stackoverflow.com/questions/14850553/javascript-regex-for-password-containing-at-least-8-characters-1-number-1-uppe
export const isPasswordValid = (password) => {
  const re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{6,}$/
  return re.test(password)
}

export const getInitialAvatar = (firstname = '', lastname = '') => {
  if (lastname === '') {
    if (firstname.split(' ').length > 1) {
      const arrFullname = firstname.split(' ')
      return (
        arrFullname[0].charAt(0).toUpperCase() +
        arrFullname[1].charAt(0).toUpperCase()
      )
    }

    return (
      firstname.charAt(0).toUpperCase() +
      firstname.charAt(firstname.length - 1).toUpperCase()
    )
  }

  if (firstname === '') {
    return (
      lastname.charAt(0).toUpperCase() +
      lastname.charAt(lastname.length - 1).toUpperCase()
    )
  }

  return firstname.charAt(0).toUpperCase() + lastname.charAt(0).toUpperCase()
}

export const isFullStorage = (user) => {
  if (user === null || user === undefined) return false

  return user.storages
    ? user.storages.used + user.retail.additionalStorageUsed >=
        user.storages.quota + user.retail.additionalStorageQuota
    : false
}
